import type { FC } from 'react';
import { useDispatch } from 'react-redux';
import APITable from '@/components/UI/Table/Reusable/APITable';
import Header from '@/components/UI/Header';
import Button from '@/components/UI/Button';
import type { TanStackColumnDef } from '@/components/UI/Table/TableComponent.model';
import type { UseTableProps, TableFilter } from '@/components/UI/Table/Reusable/APITable/APITable.model';
import type { FilterOption } from '@/components/UI/Table/TableComponent.model';
import { _notNil } from '@/littledash';
import { DateTimeRenderer } from '@/components/UI/DateRenderers/DateRenderers';
import TreatmentStatusChip from '@/components/Glossary/Sections/Treatments/TreatmentStatusChip';
import TreatmentStudyUsage from '@/components/Glossary/Sections/Treatments/TreatmentStudyUsage';
import { ApiService } from '@/support/ApiService';
import { apiErrorToast } from '@/components/UI/ApiErrorToast/ApiErrorToast';
import { notAborted } from '@/support/Hooks/fetch/useAbortController';
import { successToast } from '@/helpers';
import { ExceptionHandler } from '@/utils/ExceptionHandler';
import InVivoError from '@/model/InVivoError';
import { FilterTypes as filterType } from '@/constants/FilterTypes';
import { RouteService } from '@/support/RouteService';
import type { components } from '@/generated/internal-api/openapi-types';

type TreatmentTypeGlossary = components['schemas']['TreatmentTypeGlossaryV1.schema'];

const generateColumns = (): Array<TanStackColumnDef<TreatmentTypeGlossary>> => [
  {
    id: 'name',
    accessorKey: 'name',
    header: 'Treatment name',
    size: 300,
    isVisible: true,
    enableSorting: true,
  },
  {
    id: 'studies',
    accessorKey: 'studies',
    header: 'Used in',
    size: 120,
    isVisible: true,
    // The list endpoint only sorts by name or created_at.
    enableSorting: false,
    cell: ({ row }: { row: { original: TreatmentTypeGlossary } }) => (
      <TreatmentStudyUsage studies={row.original.studies} treatmentName={row.original.name} />
    ),
  },
  {
    id: 'created_at',
    accessorKey: 'created_at',
    header: 'Created',
    size: 150,
    isVisible: true,
    enableSorting: true,
    cell: ({ row }: { row: { original: TreatmentTypeGlossary } }) => (
      <DateTimeRenderer value={row.original.created_at} />
    ),
  },
  {
    id: 'status',
    accessorKey: 'archived_at',
    header: 'Status',
    size: 100,
    isVisible: true,
    enableSorting: false,
    cell: ({ row }: { row: { original: TreatmentTypeGlossary } }) => (
      <TreatmentStatusChip archived={_notNil(row.original.archived_at)} />
    ),
  },
];

const generateFilterOptions = (): Array<FilterOption> => [
  {
    value: 'status',
    name: 'Status',
    operations: [
      {
        name: filterType.eq,
        value: filterType.eq,
        options: [
          { value: 'active', name: 'Active' },
          { value: 'archived', name: 'Archived' },
        ],
      },
    ],
  },
];

const createDefaultActiveFilter = (): TableFilter => ({
  category: {
    name: 'Status',
    value: 'status',
    index: 0,
  },
  operation: {
    name: filterType.eq,
    index: 0,
  },
  option: {
    name: 'Active',
    value: 'active',
  },
});

/**
 * Reports a failed archive or unarchive, naming the treatments the server objected to. Both
 * requests carry `treatment_type_api_ids`, so a reported index or api_id resolves against the
 * selection that was submitted.
 */
const reportArchiveFailure = (
  error: unknown,
  treatments: Array<TreatmentTypeGlossary>,
  operation: 'archive' | 'unarchive'
): void => {
  if (!notAborted(error)) {
    return;
  }

  const title = `Could not ${operation} treatments`;

  apiErrorToast({
    title,
    error,
    labelForDetail: ({ index, field }) => {
      const byApiId = treatments.find(({ api_id }) => api_id === field);
      if (_notNil(byApiId)) {
        return byApiId.name;
      }
      return _notNil(index) ? treatments[index]?.name : undefined;
    },
  });

  ExceptionHandler.captureException(new InVivoError(title, { slug: `treatment-glossary-${operation}`, cause: error }));
};

const Treatments: FC = () => {
  const dispatch = useDispatch();
  const columns = generateColumns();
  const filterOptions = generateFilterOptions();
  const defaultFilters = [createDefaultActiveFilter()];

  const handleArchive = async (treatments: Array<TreatmentTypeGlossary>, refreshTable: () => Promise<void>) => {
    const treatmentTypeApiIds = treatments.map((treatment) => treatment.api_id);

    try {
      await ApiService.call({
        endpoint: 'POST /api/v1/team-glossary/treatment-types/archive',
        path: undefined,
        body: { treatment_type_api_ids: treatmentTypeApiIds },
        options: { onError: { toast: false, capture: false, throw: true, slug: 'treatment-glossary-archive' } },
      });

      successToast(`Successfully archived ${treatments.length} treatment${treatments.length !== 1 ? 's' : ''}`);
      await refreshTable();
    } catch (error) {
      reportArchiveFailure(error, treatments, 'archive');
    }
  };

  const handleUnarchive = async (treatments: Array<TreatmentTypeGlossary>, refreshTable: () => Promise<void>) => {
    const treatmentTypeApiIds = treatments.map((treatment) => treatment.api_id);

    try {
      await ApiService.call({
        endpoint: 'POST /api/v1/team-glossary/treatment-types/unarchive',
        path: undefined,
        body: { treatment_type_api_ids: treatmentTypeApiIds },
        options: { onError: { toast: false, capture: false, throw: true, slug: 'treatment-glossary-unarchive' } },
      });

      successToast(`Successfully unarchived ${treatments.length} treatment${treatments.length !== 1 ? 's' : ''}`);
      await refreshTable();
    } catch (error) {
      reportArchiveFailure(error, treatments, 'unarchive');
    }
  };

  const handleBulkEdit = (treatments: Array<TreatmentTypeGlossary>, refreshTable: () => void) => {
    dispatch({
      type: 'OPEN_MODAL',
      modal: 'BULK_EDIT_TREATMENTS',
      props: {
        treatments,
        onSave: async () => {
          dispatch({ type: 'CLOSE_MODAL' });
          await refreshTable();
        },
        onCancel: () => {
          dispatch({ type: 'CLOSE_MODAL' });
        },
      },
    });
  };

  const handleBulkAdd = (refreshTable: () => void) => {
    dispatch({
      type: 'OPEN_MODAL',
      modal: 'BULK_ADD_TREATMENTS',
      props: {
        onSave: async () => {
          dispatch({ type: 'CLOSE_MODAL' });
          await refreshTable();
        },
        onCancel: () => {
          dispatch({ type: 'CLOSE_MODAL' });
        },
      },
    });
  };

  const bulkActions = ({
    useTableProps: {
      selectedFlatRows,
      apiTable: { refetchTableData },
    },
  }: UseTableProps<TreatmentTypeGlossary>) => {
    const selectedTreatments = selectedFlatRows.map(({ original }) => original);

    const archivedCount = selectedTreatments.filter((item) => _notNil(item.archived_at)).length;
    const unarchivedCount = selectedTreatments.length - archivedCount;
    const hasMixedSelection = archivedCount > 0 && unarchivedCount > 0;
    const allArchived = archivedCount === selectedTreatments.length;

    const archiveAction = allArchived ? 'Unarchive' : 'Archive';
    const archiveHandler = allArchived ? handleUnarchive : handleArchive;
    const archiveDisabled = hasMixedSelection;
    const archiveTooltip = hasMixedSelection
      ? 'Cannot archive and unarchive items in the same operation. Please select only archived or only unarchived items.'
      : undefined;

    const actions = [
      {
        name: 'Edit',
        key: 'edit_treatments',
        action: () => {
          handleBulkEdit(selectedTreatments, refetchTableData);
        },
        disabled: false,
        tooltip: undefined,
        className: '',
      },
      {
        name: archiveAction,
        key: 'archive_treatments',
        action: () => {
          if (!archiveDisabled) {
            archiveHandler(selectedTreatments, refetchTableData);
          }
        },
        disabled: archiveDisabled,
        tooltip: archiveTooltip,
        className: 'red',
      },
    ];

    return actions;
  };

  const AsideComponent = ({ useTableProps }: UseTableProps<TreatmentTypeGlossary>) => {
    const {
      apiTable: { fetchTableData },
    } = useTableProps;

    return (
      <Button onClick={() => handleBulkAdd(fetchTableData)} testId="add-treatments-button">
        Add treatments
      </Button>
    );
  };

  return (
    <div>
      <Header mainHeaderText="Treatments" className="pb2" />
      <APITable
        columns={columns}
        filterOptions={filterOptions}
        apiInfo={{
          type: 'internalApi',
          route: RouteService.api({
            endpoint: 'GET /api/v1/team-glossary/treatment-types',
            path: undefined,
            query: {
              sort: 'name',
            },
          }).url,
        }}
        includeParam="studies"
        reduxTableName="treatmentGlossaries"
        queryParams={{
          sort: 'name',
        }}
        defaultSortBy={{ id: 'name', desc: false }}
        defaultFilters={defaultFilters}
        searchPlaceholderText="Search by treatment name..."
        pageSizes={[50, 100, 500, 1000]}
        defaultPageSize={50}
        testIdPrefix="treatments-v2"
        loadingText="Loading treatments..."
        noDataMessage="No treatments found"
        bulkActions={bulkActions}
        AsideComponent={AsideComponent}
      />
    </div>
  );
};

export default Treatments;
